import React, { useState, useMemo } from "react"
import { useAccessContext } from "../component/guard/AccessGuard"
import { firestore } from "../core"
import { SystemNotificationDocument, SystemNotificationReadStatusDocument, UserId } from "popopo-msg"
import { usePublicSystemNotifications, useSystemNotificationReadStatuses, markSystemNotificationAsRead } from "popopo-lib-front/firestore-access"
import { QuillViewer } from "popopo-lib-front/component"
import { Loading } from "popopo-msg"
import { formatDateTime } from "../lib/util"
import { useToast } from "popopo-lib-front/hook"

type SystemNotificationWithStatus = SystemNotificationDocument & {
  readStatus?: SystemNotificationReadStatusDocument | null
  isRead: boolean
}

export const SystemNotifications = () => {
  const context = useAccessContext()
  const { user } = context
  const userId = UserId(user.id)
  const toast = useToast()

  const notifications = usePublicSystemNotifications(firestore, 50)
  const notificationIds = useMemo(() => {
    return notifications === "loading" ? [] : notifications.map(n => n.id)
  }, [notifications])
  const readStatuses = useSystemNotificationReadStatuses(firestore, userId, notificationIds)

  const [selectedNotification, setSelectedNotification] = useState<SystemNotificationWithStatus | null>(null)

  const notificationsWithStatus: Loading<SystemNotificationWithStatus[]> = 
    notifications === "loading" || readStatuses === "loading" 
      ? "loading"
      : notifications.map(notification => {
          const readStatus = readStatuses.find(rs => rs.system_notification_id === notification.id)
          return {
            ...notification,
            readStatus,
            isRead: readStatus?.read_at != null
          }
        })

  const handleNotificationClick = async (notification: SystemNotificationWithStatus) => {
    setSelectedNotification(notification)
    
    if (!notification.isRead) {
      try {
        await markSystemNotificationAsRead(firestore, userId, notification.id)
      } catch (e) {
        console.error("Failed to mark notification as read:", e)
        toast.add({ type: "danger", title: "お知らせ", message: "既読マークに失敗しました" })
      }
    }
  }

  return (
    <>
      {notificationsWithStatus === "loading" ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">読み込み中...</span>
          </div>
        </div>
      ) : notificationsWithStatus.length === 0 ? (
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          現在公開中のお知らせはありません。
        </div>
      ) : (
        <div className="list-group">
          {notificationsWithStatus.map((notification, index) => (
            <button
              key={notification.id}
              className={`list-group-item list-group-item-action py-3 ${!notification.isRead ? 'list-group-item-primary' : ''} ${0 < index ? 'border-top' : ''}`}
              style={{ marginBottom: '8px' }}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="d-flex w-100 justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  {!notification.isRead && (
                    <span className="badge bg-primary me-2">未読</span>
                  )}
                  <h6 className="mb-0 me-2">{notification.title}</h6>
                </div>
                <small className="text-muted">
                  {formatDateTime(notification.display_period.start_at)}
                </small>
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedNotification && (
        <div className="modal show" style={{ display: 'block' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedNotification.title}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSelectedNotification(null)}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <small className="text-muted">
                    投稿日時: {formatDateTime(selectedNotification.display_period.start_at)}
                  </small>
                </div>
                <div className="quill-content">
                  <QuillViewer content={selectedNotification.content} />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setSelectedNotification(null)}
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
